// Lossless selection, not a second evidence classifier. Missing input keys
// remain absent; nulls retain their original meaning. No snapshot fallback.
const own = (obj, key) => obj && Object.prototype.hasOwnProperty.call(obj, key);
const escapePointer = key => String(key).replace(/~/g, '~0').replace(/\//g, '~1');
const interpretation = Object.freeze({
  kind: 'interpretation_rules_not_measurements',
  reproduction: 'An observed threshold crossing establishes a measured symptom in the stated scope, not its cause. Counts from overlapping scopes must not be added.',
  profileAgreement: 'Agreement on an Event Timing phase does not identify a responsible script or element.',
  causalEvidence: 'When unconfirmed or unknown, do not name a confirmed cause or prescribe a specific element repair. Candidates may guide further investigation only.',
  field: 'Field p75 and laboratory timings describe different populations and conditions; do not substitute one for the other.',
  missing: 'Absent or null data is unknown, not zero, fast, successful, or verified.',
  source: 'All evidence values are copied from evidenceContract; interpretation rules are not evidence.'
});

export function projectAuditEvidenceDigest(contract) {
  if (!contract || !Array.isArray(contract.records)) return { digest: null, sources: {} };
  const sources = {};
  const copy = (value, from, to) => {
    if (Array.isArray(value)) return value.map((v, i) => copy(v, `${from}/${i}`, `${to}/${i}`));
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value)
      .map(([k, v]) => [k, copy(v, `${from}/${escapePointer(k)}`, `${to}/${escapePointer(k)}`)]));
    sources[to] = from;
    return value;
  };
  const pick = (obj, keys, from, to = from) => Object.fromEntries(keys.filter(k => own(obj, k))
    .map(k => [k, copy(obj[k], `${from}/${k}`, `${to}/${k}`)]));
  const digest = pick(contract, [
    'version', 'snapshotRef', 'domain', 'url', 'measured_at',
    'measurement_version', 'collector', 'consent', 'knownGaps'
  ], '');
  digest.records = contract.records.map((r, i) => {
    const p = `/records/${i}`;
    return {
      ...pick(r, ['aggregateValue', 'primarySeriesRef'], p),
      scope: pick(r.scope, ['metric', 'device', 'kind', 'url', 'validRuns'], `${p}/scope`),
      reproduction: pick(r.reproduction, ['state', 'reason', 'threshold', 'unit', 'aboveThresholdRuns'], `${p}/reproduction`),
      profileAgreement: pick(r.profileAgreement, ['state', 'reason', 'dominantPhaseCounts'], `${p}/profileAgreement`),
      causalEvidence: pick(r.causalEvidence, ['state', 'reason', 'missingLinks'], `${p}/causalEvidence`)
    };
  });
  if (Array.isArray(contract.fieldRecords)) digest.fieldRecords = contract.fieldRecords.map((r, i) =>
    pick(r, ['metric', 'device', 'scope', 'url', 'origin', 'p75', 'resultSeverity', 'collectionPeriod'], `/fieldRecords/${i}`));
  if (contract.crossPageByMetric) digest.crossPageByMetric = Object.fromEntries(
    Object.entries(contract.crossPageByMetric).map(([metric, r]) => [metric, pick(r, [
      'observedPages', 'denominatorReason', 'sameSelectorPages', 'templateAttributed',
      'crossPageObservation', 'eligiblePageDenominator', 'sameMechanismAttributed',
      'sameSelectorObservation', 'templateAttributedPages', 'sameMechanismAttributedPages'
    ], `/crossPageByMetric/${escapePointer(metric)}`)]));
  if (Array.isArray(contract.findings)) digest.findings = contract.findings.map((r, i) =>
    pick(r, ['code', 'metric', 'device', 'status', 'evidenceStrength', 'legacyEvidenceStrength', 'owner', 'causalEvidence'], `/findings/${i}`));
  // Conditions carry explicit unknown reasons and consent provenance.
  if (contract.auditConditions) digest.auditConditions = pick(contract.auditConditions,
    ['requestedCmpMode', 'effectiveCmpMode', 'cmpClicked'], '/auditConditions');
  digest.interpretation = { ...interpretation };
  return { digest, sources };
}

export const buildAuditEvidenceDigest = contract => projectAuditEvidenceDigest(contract).digest;

export function embedAuditEvidenceDigest(html, contract) {
  const digest = buildAuditEvidenceDigest(contract);
  if (!digest || typeof html !== 'string') return html;
  const json = JSON.stringify(digest).replace(/</g, '\\u003c').replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
  const block = `<script type="application/json" id="controlvitals-evidence">${json}</script>`;
  // Idempotent when a saved export is served through a customer delivery route.
  const existing = /<script\b[^>]*\bid=["']controlvitals-evidence["'][^>]*>[\s\S]*?<\/script>/i;
  if (existing.test(html)) return html.replace(existing, () => block);
  return html.replace(/<\/body>/i, match => `${block}${match}`);
}
